import { UserPen } from "lucide-react"
import { useForm } from "react-hook-form"
import { toast } from "sonner"

import { Button } from "@components/ui/button"
import { useAuthUserDetails } from "@apis/users/useUsers"
import { useDrawer } from "@contexts/DrawerContext"
import { Form } from "@components/ui/form"
import { VerificationChecklist } from "@components/VerificationChecklist"
import { getNewOrderVerificationScript } from "@components/ProspectNew/NewOrderFunnel/utils/newOrderVerificationScript"
import { AccountContactPreferencesForm } from "@components/AccountNew/AccountContactPreferencesForm/AccountContactPreferencesForm"
import {
  serializeScript,
  type TVerificationForm,
} from "@utils/verificationScriptsUtils"
import { useNewOrderFunnel } from "@components/ProspectNew/NewOrderFunnel/utils/useNewOrderFunnel"
import { useCreateOrder } from "@apis/orders/useOrders"
import { useGetAddressQuery } from "@apis/address/useAddress"
import type { IOrderPayload } from "@commonTypes/order"
import type { IShopper } from "@apis/shoppers/types"
import type { IMarket } from "@apis/accounts/types"
import type { IOffer } from "@commonTypes/offer"

type TNewOrderVerificationFormProps = {
  prospect_guid: string
  offer: IOffer
  shopper: IShopper
  market: IMarket
}

export const NewOrderVerificationForm = ({
  prospect_guid,
  offer,
  shopper,
  market,
}: TNewOrderVerificationFormProps) => {
  const authUserDetails = useAuthUserDetails()
  const {
    setStep,
    selectedAddress,
    setOrder,
    verificationCompleted,
    setVerificationCompleted,
    order,
    entrySource,
  } = useNewOrderFunnel()
  const { openDrawer } = useDrawer()
  const createOrderMutation = useCreateOrder()
  const getAddresQuery = useGetAddressQuery(selectedAddress?.address_guid)

  const script = serializeScript(
    getNewOrderVerificationScript({ offer, shopper, market }),
  )

  const form = useForm<TVerificationForm>({
    defaultValues: {
      verificationItem: script?.reduce(
        (acc, _, index) => {
          acc[index.toString()] = false
          return acc
        },
        {} as Record<string, boolean>,
      ),
    },
    mode: "onChange",
  })

  const onSubmit = () => {
    const payload: Partial<IOrderPayload> = {
      account_number: shopper.account_number,
      ack_can_text: shopper.ack_can_text,
      agent_id: authUserDetails.data?.email,
      agent_name: authUserDetails.data?.email,
      business_name: shopper.business_name,
      campaign_id: shopper.campaign_id,
      channel_code:
        entrySource === "newOrder" ? "IB_SALES" : shopper.channel_code,
      contact_first_name: shopper.contact_first_name,
      contact_last_name: shopper.contact_last_name,
      conversion_medium: "INSPIRE_CRM",
      email: shopper.email,
      enrollment_type:
        offer?.product_type_code?.toUpperCase() === "NaturalGas"
          ? "GAS"
          : "ELECTRIC",
      first_name: shopper.first_name,
      last_name: shopper.last_name,
      mailing_address_id: shopper.mailing_address_id,
      offer_code: offer.offer_code,
      primary_phone: shopper.primary_phone,
      prospect_guid: shopper.prospect_guid,
      revenue_class_code: offer.revenue_class_code,
      service_address: getAddresQuery?.data,
      service_address_id: getAddresQuery?.data?.address_id,
      shipping_address_id: getAddresQuery?.data?.address_id, // Same as service_address_id at this point
      shopper_guid: shopper.shopper_guid as string,
      source_code: offer.source_code as string,
      title: shopper.title,
      tpv_type: shopper.tpv_type,
      tpv_vendor: shopper.tpv_vendor,
    }

    createOrderMutation.mutate(payload, {
      onSuccess: (order) => {
        setOrder(order)
        setVerificationCompleted(true)
        setStep("confirmation")
      },
      onError: (error) => toast.error(`Error creating the order! ${error}`),
    })
  }

  return (
    <div className="tw:flex tw:flex-col tw:gap-4">
      <h2 className="tw:mb-0 tw:text-xl tw:font-semibold">Are You Sure?</h2>

      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="tw:flex tw:flex-col tw:gap-2"
        >
          <VerificationChecklist
            script={script}
            form={form}
            completed={verificationCompleted}
          />

          <div className="tw:flex tw:justify-between">
            <Button
              variant="warning"
              onClick={() => alert("go back button")}
              type="button"
            >
              Go Back
            </Button>

            <div className="tw:flex tw:gap-3">
              <Button
                type="button"
                onClick={() =>
                  openDrawer(
                    <AccountContactPreferencesForm
                      prospect_guid={prospect_guid}
                    />,
                    "Update Contact Preferences",
                  )
                }
                variant="outline"
              >
                <UserPen />
              </Button>

              <Button
                type="submit"
                disabled={
                  (!form.formState.isValid && !verificationCompleted) || !!order
                }
              >
                Submit Order
              </Button>
            </div>
          </div>
        </form>
      </Form>
    </div>
  )
}
